import React from "react";
import type { FundingApplicationSchemas } from "api-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { AnalyticsScreenProvider, useMediaQuery, OnboardingLayout } from "@wayflyer/flyui";
import { IconArrowLeft24Line, IconArrowRight24Line } from "@wayflyer/flyui-icons/24/line";
import { usePostHog } from "posthog-js/react";
import { useIntl } from "react-intl";
import { getStageCompletionStatusQueryOptions, useSpaNavigate } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { COMMON_MESSAGES } from "../../components/common-messages";
import { NAVIGATION_PATHS } from "../../components/constants";
import { PAGE_TYPE, useFundingApplicationNavigation } from "../../hooks/navigation-next";
import {
  getFundingApplicationStagesQueryOptions,
  getIsFundingApplicationSubmittableQueryOptions,
} from "../../queries/funding-application-configuration/queries";
import { useFundingApplication, useFundingApplicationStateMutation } from "../../queries/funding-application/queries";

import { NotReadyModal } from "./components";
import { ApplicationSummaryNext } from "./application-summary-next";
import { MESSAGES } from "./message-definitions";

interface ApplicationSummaryPageNextProps {
  fundingApplicationStages: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen;
}

const ApplicationSummaryPageNext = ({ fundingApplicationStages }: ApplicationSummaryPageNextProps) => {
  const intl = useIntl();
  const posthog = usePostHog();
  const query = useMediaQuery();
  const isMobile = query("sm").windowLte;
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: fundingApplication } = useFundingApplication(customerDomain);
  const submittableQueryOptions = getIsFundingApplicationSubmittableQueryOptions(customerDomain, fundingApplication.id);
  const { data: isSubmittable } = useSuspenseQuery(submittableQueryOptions);
  const setApplicationState = useFundingApplicationStateMutation(customerDomain);
  const { data: stageCompletionStatus } = useSuspenseQuery(getStageCompletionStatusQueryOptions(customerDomain));
  const { navigate } = useSpaNavigate("FundingApp");
  const [isNotReadyModalOpen, setIsNotReadyModalOpen] = React.useState(false);
  const navigationContext = useFundingApplicationNavigation({
    currentPage: PAGE_TYPE.SUMMARY,
  });

  const onSubmit = () => {
    if (isSubmittable) {
      setApplicationState.mutateAsync({ applicationState: "submitted" }).then(() => {
        posthog?.capture("onboarding_submission_started", {
          outcome: "submitted",
          areStagesSatisfied: stageCompletionStatus,
        });
        navigate(NAVIGATION_PATHS.IN_REVIEW);
      });
    } else {
      posthog?.capture("onboarding_submission_started", {
        outcome: "blocked",
        areStagesSatisfied: stageCompletionStatus,
      });
      setIsNotReadyModalOpen(true);
    }
  };

  return (
    <AnalyticsScreenProvider screen="onboarding-application-summary">
      <NotReadyModal isOpen={isNotReadyModalOpen} setIsOpen={setIsNotReadyModalOpen} />
      <OnboardingLayout
        variant="wide"
        title={intl.formatMessage(MESSAGES.reviewAndSubmitTitle)}
        subtitle={intl.formatMessage(MESSAGES.connectionsSummaryInformationText)}
        actions={{
          primary: {
            content: intl.formatMessage(MESSAGES.submitApplicationLabel),
            icon: IconArrowRight24Line,
            onAction: onSubmit,
            analyticsId: "application-summary-submit-button",
          },
          secondary: {
            content: isMobile ? undefined : intl.formatMessage(COMMON_MESSAGES.backLabel),
            icon: isMobile ? IconArrowLeft24Line : undefined,
            onAction: () => {
              if (navigationContext.previousPath) {
                navigationContext.navigate(navigationContext.previousPath);
              }
            },
            analyticsId: "application-summary-back-button",
          },
        }}
      >
        <ApplicationSummaryNext fundingApplicationStages={fundingApplicationStages} />
      </OnboardingLayout>
    </AnalyticsScreenProvider>
  );
};

export const ApplicationSummaryPage = () => {
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: fundingApplicationStages } = useSuspenseQuery(getFundingApplicationStagesQueryOptions(customerDomain));

  return <ApplicationSummaryPageNext fundingApplicationStages={fundingApplicationStages} />;
};
